import React from 'react';
import {
  Flag, Filter, HeartPulse, Layers, Ban, ShieldCheck, Brain,
  BookOpen, Check, ChevronRight, Info,
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

// ─── Client-portal Status Pipeline (Jun 2026 · full Admin parity) ────
// Renders the SAME explainable funnel the Admin Workbench shows:
//
//   China Universe → Phase Filter → TA Filter → Modality Exclusion →
//   Other Exclusions → Eligible for AI → AI Assessed →
//   Committee Review → Finalised
//
// Stage data comes from the backend `/pipeline` endpoint's `stages`
// array (compute_universe_pipeline). Deterministic filter stages are
// informational (excluded assets never reach the client lists); the
// AI-side stages are clickable and filter the lists below.

const STAGE_META = {
  universe: {
    icon: Flag, clickable: false,
    definition: 'Every asset from a China-based biotech company. This is the starting point of every screening run.',
  },
  phase_filter: {
    icon: Filter, clickable: false,
    definition: "Removes assets that don't meet the Include-phase rule (e.g. Phase 3+). The number shown is what remains after this filter.",
  },
  ta_filter: {
    icon: HeartPulse, clickable: false,
    definition: "Removes assets whose therapeutic area matches the declared off-strategy TAs (e.g. Oncology). The number shown is what remains.",
  },
  modality_exclusion: {
    icon: Layers, clickable: false,
    definition: 'Removes assets built on excluded modalities (e.g. Cell therapy, CAR-T, siRNA, Vaccines). The number shown is what remains.',
  },
  other_exclusion: {
    icon: Ban, clickable: false,
    definition: 'Removes assets excluded by Asset Type, Licensing Status, or Keyword rules. The number shown is what remains.',
  },
  eligible: {
    icon: ShieldCheck, clickable: true,
    definition: 'Assets that survived every deterministic rule and are ready for AI assessment. This is the Reference Library universe.',
  },
  ai_scored: {
    icon: Brain, clickable: true,
    definition: 'Eligible assets that Perplexity + Claude have fully scored and given a recommendation tier.',
  },
  committee_review: {
    icon: BookOpen, clickable: true,
    definition: 'Active-review tiers (Strategic Priority + High Priority + Medium) currently with the committee for scoring. Monitor / Low Priority / Not Suitable are Reference-only.',
  },
  finalised: {
    icon: Check, clickable: true,
    definition: 'Assets where the committee has issued a final decision (Approved / Rejected).',
  },
};

// Legacy 5-stage fallback (used only until the backend returns `stages`).
const LEGACY_STAGES = [
  { key: 'cohort',           label: 'China Universe',   metaKey: 'universe' },
  { key: 'phase_3_plus',     label: 'Eligible for AI',  metaKey: 'eligible' },
  { key: 'ta_of_interest',   label: 'AI Assessed',      metaKey: 'ai_scored' },
  { key: 'committee_review', label: 'Committee Review', metaKey: 'committee_review' },
  { key: 'finalised_assets', label: 'Finalised',        metaKey: 'finalised' },
];

function StageInfoTip({ text }) {
  return (
    <Tooltip delayDuration={150}>
      <TooltipTrigger asChild>
        <span
          role="button"
          tabIndex={-1}
          aria-label="Definition"
          className="inline-flex items-center justify-center text-slate-400 hover:text-slate-700 transition-colors cursor-help"
          onClick={(e) => e.stopPropagation()}
        >
          <Info className="w-3 h-3" />
        </span>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        className="max-w-[280px] bg-slate-900 text-white text-[11px] leading-snug px-3 py-2"
      >
        {text}
      </TooltipContent>
    </Tooltip>
  );
}

function PipelineArrow({ active }) {
  return (
    <div className="flex items-center shrink-0 mt-[20px]" aria-hidden="true">
      <div className={`h-0.5 w-2 lg:w-4 ${active ? 'bg-emerald-400' : 'bg-slate-200'}`} />
      <ChevronRight className={`w-3.5 h-3.5 -ml-1 ${active ? 'text-emerald-500' : 'text-slate-300'}`} />
    </div>
  );
}

/**
 * ClientStatusPipeline
 *
 * @param {object} props
 * @param {object} props.counts        — legacy counts payload (fallback)
 * @param {Array}  props.stages        — full funnel from the backend `/pipeline` endpoint
 * @param {string|null} props.activeStage
 * @param {(stageKey:string|null)=>void} props.onStageClick
 * @param {string} [props.cohortName]
 */
export default function ClientStatusPipeline({
  counts = {},
  stages = [],
  activeStage = null,
  onStageClick = () => {},
  cohortName = '',
}) {
  let stageStates;
  if (stages && stages.length > 0) {
    stageStates = stages.map((s) => {
      const meta = STAGE_META[s.key] || {};
      return {
        key: s.key,
        label: s.label,
        count: s.count ?? 0,
        excluded: s.excluded ?? 0,
        isFilter: (s.section || '') === 'database' && s.key !== 'universe',
        icon: meta.icon || Flag,
        clickable: !!meta.clickable,
        definition: meta.definition || s.hint || '',
      };
    });
  } else {
    stageStates = LEGACY_STAGES.map((s) => {
      const meta = STAGE_META[s.metaKey] || {};
      return {
        key: s.key,
        label: s.label,
        count: counts[s.key] ?? 0,
        excluded: 0,
        isFilter: false,
        icon: meta.icon || Flag,
        clickable: true,
        definition: meta.definition || '',
      };
    });
  }

  const totalAssets = (stages.length > 0
    ? (stageStates[0]?.count ?? 0)
    : (counts.cohort ?? 0));
  const poolLabel = `China Universe · ${totalAssets.toLocaleString()} assets`;

  return (
    <TooltipProvider delayDuration={150}>
    <div
      className="bg-white border border-slate-200 rounded-lg px-4 py-4"
      data-testid="client-status-pipeline"
    >
      <div className="flex items-baseline justify-between mb-3">
        <div className="text-[11px] uppercase tracking-wide font-semibold text-slate-500 inline-flex items-baseline gap-1.5">
          Screening Funnel
          <span className="text-[10px] normal-case tracking-normal text-slate-500 font-normal">
            ({poolLabel})
          </span>
        </div>
        {activeStage && (
          <button
            type="button"
            onClick={() => onStageClick(null)}
            className="text-[10px] text-indigo-600 hover:text-indigo-800 underline"
            data-testid="client-pipeline-clear"
          >
            clear filter
          </button>
        )}
      </div>

      <div className="flex items-start gap-0 w-full overflow-x-auto">
        {stageStates.map((s, idx) => {
          const Icon = s.icon;
          const isActive = activeStage === s.key;
          const completed = (s.count ?? 0) > 0;
          // Deterministic filter stages render in sky (informational);
          // AI-side stages keep the emerald "progress" language.
          const ringCls = s.isFilter
            ? 'bg-sky-50 ring-2 ring-sky-200'
            : completed
              ? 'bg-emerald-100 ring-2 ring-emerald-400'
              : 'bg-slate-100 ring-2 ring-slate-200';
          const iconColorCls = s.isFilter
            ? 'text-sky-600'
            : completed ? 'text-emerald-700' : 'text-slate-400';
          const labelCls = (s.isFilter
              ? 'text-slate-600'
              : completed ? 'text-emerald-800 font-semibold' : 'text-slate-500')
            + (isActive ? ' underline' : '');
          const countBadgeCls = s.isFilter
            ? 'bg-sky-500 text-white'
            : completed ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-500';

          return (
            <React.Fragment key={s.key}>
              <button
                type="button"
                onClick={() => s.clickable && onStageClick(isActive ? null : s.key)}
                className={`flex flex-col items-center gap-1 px-0.5 py-1 flex-1 min-w-[74px] ${
                  s.clickable ? 'hover:opacity-80 cursor-pointer' : 'cursor-default'
                }`}
                data-testid={`client-pipeline-stage-${s.key}`}
              >
                <div className="relative">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${ringCls}`}>
                    <Icon className={`w-4.5 h-4.5 w-[18px] h-[18px] ${iconColorCls}`} />
                  </div>
                  <span
                    className={`absolute -top-1.5 -right-2.5 min-w-[24px] h-[18px] px-1 rounded-full text-[10px] font-semibold flex items-center justify-center ${countBadgeCls}`}
                    data-testid={`client-pipeline-stage-${s.key}-count`}
                  >
                    {(s.count ?? 0).toLocaleString()}
                  </span>
                </div>
                <div className={`text-[10px] leading-tight text-center inline-flex items-center justify-center gap-0.5 ${labelCls}`}>
                  <span className="max-w-[86px]">{s.label}</span>
                  <StageInfoTip text={s.definition} />
                </div>
                {s.isFilter && (s.excluded ?? 0) > 0 && (
                  <div className="text-[9px] text-rose-500 font-medium leading-none" data-testid={`client-pipeline-stage-${s.key}-excluded`}>
                    −{s.excluded.toLocaleString()} excluded
                  </div>
                )}
              </button>
              {idx < stageStates.length - 1 && (
                <PipelineArrow active={!s.isFilter && (s.count ?? 0) > 0} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
    </TooltipProvider>
  );
}
